import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Youtube, Linkedin, Send, ChevronDown, Search } from 'lucide-react';
import SEO from '../components/SEO';

const contactInfo = {
  address: {
    line1: '9th Floor Office No 903-03',
    line2: 'MISA Business Center Dubai',
    line3: 'Iris Bay - Business Bay',
    line4: 'Dubai, UAE',
  },
  phones: ['+971 5858 6 2014', '+971 5858 7 2014'],
  email: 'support@goldentreeawards.com',
  hours: 'Sunday - Friday: 9:00 AM - 9:00 PM (Dubai Time)',
};

const socialLinks = [
  { icon: Instagram, url: 'https://www.instagram.com/goldentreeeventsdubai/', label: 'Instagram' },
  { icon: Facebook, url: 'https://www.facebook.com/AwardsByGoldenTreeDubai', label: 'Facebook' },
  { icon: Youtube, url: 'https://www.youtube.com/channel/UCar_yBf569VvrfVy4V-qSXg/videos', label: 'YouTube' },
  { icon: Linkedin, url: 'https://linkedin.com/company/goldentreeeventsdubai', label: 'LinkedIn' },
];

const enquiryTypes = [
  'Event Enquiry / Hosting Events',
  'Awards Participation',
  'Sponsorship',
  'Exhibit at Expo',
  'Partnership',
  'Others',
];

const countryCodes = [
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+1', country: 'USA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+974', country: 'Qatar', flag: '🇶🇦' },
  { code: '+965', country: 'Kuwait', flag: '🇰🇼' },
  { code: '+973', country: 'Bahrain', flag: '🇧🇭' },
  { code: '+968', country: 'Oman', flag: '🇴🇲' },
  { code: '+962', country: 'Jordan', flag: '🇯🇴' },
  { code: '+961', country: 'Lebanon', flag: '🇱🇧' },
  { code: '+20', country: 'Egypt', flag: '🇪🇬' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  { code: '+234', country: 'Nigeria', flag: '🇳🇬' },
  { code: '+254', country: 'Kenya', flag: '🇰🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
  { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽' },
  { code: '+90', country: 'Turkey', flag: '🇹🇷' },
  { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
  { code: '+880', country: 'Bangladesh', flag: '🇧🇩' },
  { code: '+94', country: 'Sri Lanka', flag: '🇱🇰' },
  { code: '+977', country: 'Nepal', flag: '🇳🇵' },
];

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+971',
    whatsapp: '',
    enquiryType: '',
    message: '',
  });
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const filteredCountries = countryCodes.filter(
    (c) =>
      c.country.toLowerCase().includes(countrySearch.toLowerCase()) ||
      c.code.includes(countrySearch)
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        countryCode: '+971',
        whatsapp: '',
        enquiryType: '',
        message: '',
      });
    }, 1500);
  };

  const selectedCountry = countryCodes.find((c) => c.code === formData.countryCode);

  return (
    <main data-testid="contact-page" className="pt-24 min-h-screen bg-[#0a0a0a]">
      <SEO 
        title="Contact Us - Get in Touch with Golden Tree Events"
        description="Contact Golden Tree Events for event enquiries, award nominations, sponsorships, or partnerships. Dubai-based international event management company serving 50+ countries."
        keywords="contact Golden Tree Events, event enquiry Dubai, award nomination, sponsorship enquiry, event partnership"
      />
      {/* Hero Section */}
      <section className="py-16 md:py-24 border-b border-white/10">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Get In Touch</p>
            <h1 className="font-montserrat text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
              Contact{' '}
              <span className="gradient-gold">Us</span>
            </h1>
            <p className="font-google-sans text-[#A1A1AA] text-lg max-w-2xl mx-auto">
              Have questions about nominations, events, or partnerships? 
              We're here to help you achieve excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="font-montserrat text-2xl text-white mb-8">Contact Information</h2>
              
              <div className="space-y-8">
                {/* Address */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-google-sans text-white font-medium mb-2">Office Address</h3>
                    <p className="text-[#A1A1AA] text-sm leading-relaxed">
                      Golden Tree Events LLC<br />
                      {contactInfo.address.line1}<br />
                      {contactInfo.address.line2}<br />
                      {contactInfo.address.line3}<br />
                      {contactInfo.address.line4}
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-google-sans text-white font-medium mb-2">Phone Numbers</h3>
                    <div className="space-y-1">
                      {contactInfo.phones.map((phone) => (
                        <a
                          key={phone}
                          href={`tel:${phone.replace(/\s/g, '')}`}
                          className="block text-[#A1A1AA] text-sm hover:text-[#D4AF37] transition-colors"
                        >
                          {phone}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-google-sans text-white font-medium mb-2">Email Address</h3>
                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="text-[#A1A1AA] text-sm hover:text-[#D4AF37] transition-colors"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-google-sans text-white font-medium mb-2">Business Hours</h3>
                    <p className="text-[#A1A1AA] text-sm">{contactInfo.hours}</p>
                  </div>
                </div>

                {/* Social Links */}
                <div>
                  <h3 className="font-google-sans text-white font-medium mb-4">Follow Us</h3>
                  <div className="flex items-center gap-4">
                    {socialLinks.map((social) => (
                      <a
                        key={social.label}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`contact-social-${social.label.toLowerCase()}`}
                        className="w-12 h-12 border border-white/20 flex items-center justify-center text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
                        aria-label={social.label}
                      >
                        <social.icon size={20} />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Enquiry Form */}
            <div>
              <h2 className="font-montserrat text-2xl text-white mb-8">Enquiry Form</h2>
              
              {submitted ? (
                <div className="p-8 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg text-center">
                  <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send className="w-8 h-8 text-black" />
                  </div>
                  <h3 className="font-montserrat text-white text-xl mb-2">Thank You!</h3>
                  <p className="text-[#A1A1AA]">Your enquiry has been submitted successfully. We'll get back to you soon.</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-4 text-[#D4AF37] hover:underline"
                  >
                    Submit another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Person Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter your full name"
                      data-testid="enquiry-name"
                      className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 text-white placeholder:text-[#52525B] focus:border-[#D4AF37] focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Email ID *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter your email address"
                      data-testid="enquiry-email"
                      className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 text-white placeholder:text-[#52525B] focus:border-[#D4AF37] focus:outline-none transition-colors"
                    />
                  </div>

                  {/* WhatsApp Number with ISD */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">WhatsApp Number *</label>
                    <div className="flex gap-2">
                      {/* Country Code Dropdown */}
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                          data-testid="country-code-dropdown"
                          className="flex items-center gap-2 px-3 py-3 bg-white/[0.02] border border-white/10 text-white min-w-[120px] hover:border-[#D4AF37] transition-colors"
                        >
                          <span>{selectedCountry?.flag}</span>
                          <span>{formData.countryCode}</span>
                          <ChevronDown className="w-4 h-4 ml-auto" />
                        </button>
                        
                        {showCountryDropdown && (
                          <div className="absolute top-full left-0 mt-1 w-64 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl z-50 max-h-64 overflow-hidden">
                            {/* Search */}
                            <div className="p-2 border-b border-white/10">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                                <input
                                  type="text"
                                  placeholder="Search country..."
                                  value={countrySearch}
                                  onChange={(e) => setCountrySearch(e.target.value)}
                                  className="w-full pl-9 pr-3 py-2 bg-white/[0.02] border border-white/10 text-white text-sm placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]"
                                />
                              </div>
                            </div>
                            {/* Country List */}
                            <div className="max-h-48 overflow-y-auto">
                              {filteredCountries.map((country) => (
                                <button
                                  key={country.code}
                                  type="button"
                                  onClick={() => {
                                    setFormData({ ...formData, countryCode: country.code });
                                    setShowCountryDropdown(false);
                                    setCountrySearch('');
                                  }}
                                  className="w-full flex items-center gap-3 px-3 py-2 text-left text-white hover:bg-[#D4AF37]/10 transition-colors"
                                >
                                  <span>{country.flag}</span>
                                  <span className="text-sm">{country.country}</span>
                                  <span className="text-[#A1A1AA] text-sm ml-auto">{country.code}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Phone Input */}
                      <input
                        type="tel"
                        required
                        value={formData.whatsapp}
                        onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                        placeholder="Enter WhatsApp number"
                        data-testid="enquiry-whatsapp"
                        className="flex-1 px-4 py-3 bg-white/[0.02] border border-white/10 text-white placeholder:text-[#52525B] focus:border-[#D4AF37] focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Enquiry Type */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Type *</label>
                    <select
                      required
                      value={formData.enquiryType}
                      onChange={(e) => setFormData({ ...formData, enquiryType: e.target.value })}
                      data-testid="enquiry-type"
                      className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none transition-colors appearance-none cursor-pointer"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23D4AF37' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '16px' }}
                    >
                      <option value="" className="bg-[#1a1a1a]">Select enquiry type</option>
                      {enquiryTypes.map((type) => (
                        <option key={type} value={type} className="bg-[#1a1a1a]">{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Message (Optional) */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Message (Optional)</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us more about your enquiry..."
                      rows={4}
                      data-testid="enquiry-message"
                      className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 text-white placeholder:text-[#52525B] focus:border-[#D4AF37] focus:outline-none transition-colors resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="enquiry-submit"
                    className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-medium px-6 py-4 uppercase tracking-widest text-sm hover:bg-[#C5A028] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Submit Enquiry
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
